import React, { useState } from 'react';
import { TextField as MUITextField, Fab } from '@mui/material';
import { useSelector } from 'react-redux';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import { Icon } from 'leaflet';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import MapIcon from '@mui/icons-material/Map';
import TEXT from './text';
const markerIcon = new Icon({
    iconUrl: "crosshair.png",
    iconSize: [38, 38],
});

const LocationMarker = ({ setMapPosition, setGeoLocation, mapRef }) => {
    const map = useMapEvents({
        click(e) {
            setMapPosition(e.latlng);
            setGeoLocation(`${e.latlng.lat},${e.latlng.lng}`);
        },
    });

    mapRef.current = map; // Store the map instance in the ref

    return null;
};

const MapAddressComponent = ({ properties, value, valueUpdaterCallback, validityUpdaterCallback }) => {
    const language = useSelector(state => state.App_Settings.language);
    const { options = {} } = properties;

    const [geoLocation, setGeoLocation] = useState(value !== undefined ? value : (options.defaultValue || options.value || ''));
    const [mapPosition, setMapPosition] = useState(null);
    const [error, setError] = useState(false);
    const mapRef = React.useRef(null); // Ref to store the map instance

    // Sync with parent value changes
    React.useEffect(() => {
        if (value !== undefined && value !== geoLocation) {
            setGeoLocation(value);
            const isValidFormat = validateInput(value);
            if (isValidFormat) {
                const [lat, lon] = value.split(',').map(parseFloat);
                setMapPosition([lat, lon]);
            }
        }
    }, [value]);

    const validateInput = (value) => {
        const pattern = /^\d+(?:\.\d+)?,\d+(?:\.\d+)?$/;
        return pattern.test(value);
    };

    const handleInputChange = (e) => {
        const newValue = e.target.value;
        setGeoLocation(newValue);

        const isValid = validateInput(newValue);
        if (!isValid) {
            setError(true);
        } else {
            setError(false);
            const [lat, lon] = newValue.split(',').map(parseFloat);
            setMapPosition([lat, lon]);
        }

        // Notify parent component
        if (valueUpdaterCallback) {
            valueUpdaterCallback(newValue);
        }

        // Update validity
        if (validityUpdaterCallback) {
            const validityStatus = options.required ? (newValue.trim() !== '' && isValid) : true;
            validityUpdaterCallback(validityStatus);
        }

        if (options.onChange) {
            options.onChange(newValue);
        }
    };

    const fillWithCurrentLocation = () => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition((position) => {
                const newGeoLocation = `${position.coords.latitude},${position.coords.longitude}`;
                setGeoLocation(newGeoLocation);
                setMapPosition([position.coords.latitude, position.coords.longitude]);
                setError(false);
                if (mapRef.current) {
                    mapRef.current.flyTo([position.coords.latitude, position.coords.longitude], 15, { duration: 1.5 });
                }
                if (options.onChange) {
                    options.onChange(newGeoLocation);
                }
            });
        }
    };

    const checkDirectionOnMap = () => {
        const direction = geoLocation;
        window.open(`https://www.google.com/maps/dir//${direction}`);
    };

    return (
        <div>
            <MUITextField
                label={options.label || TEXT.MapAddress[language]}
                variant={options.variant || 'outlined'}
                value={geoLocation}
                onChange={handleInputChange}
                placeholder={options.placeholder || ''}
                fullWidth={options.fullWidth || true}
                required={options.required || false}
                error={error}
                helperText={error ? TEXT.InvalidGeoLocation[language] || "Input must match 'latitude,longitude' format" : ''}
            />
            <MapContainer center={[27.176469131898898, 31.18359368294478]} zoom={5} scrollWheelZoom={false} style={{ height: "300px", marginTop: "16px" }}>
                <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {mapPosition && <Marker position={mapPosition} icon={markerIcon} />}
                <LocationMarker setMapPosition={setMapPosition} setGeoLocation={setGeoLocation} mapRef={mapRef} />
            </MapContainer>
            <div style={{ textAlign: "center", marginTop: "16px" }}>
                <Fab color="primary" aria-label="Current Location" size="small" onClick={fillWithCurrentLocation}>
                    <MyLocationIcon />
                </Fab>
                <span> </span>
                <Fab color="primary" aria-label="View on Google Maps" size="small" onClick={checkDirectionOnMap}>
                    <MapIcon />
                </Fab>
            </div>
        </div>
    );
};

export default MapAddressComponent;
